import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Stack
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Pending as PendingIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import {
  downloadAuthenticatedPdf,
  openAuthenticatedPdfViaToken,
  viewAuthenticatedPdf
} from '../../utils/authenticatedPdf';
import MaintenanceWorkflowProgressIndicator from './MaintenanceWorkflowProgressIndicator';
import WorkOrderSecondPageDialog from './WorkOrderSecondPageDialog';
import { ROLES } from '../../constants/roles';
import { formatDateTimeWithTimezone } from '../../utils/timezoneUtils';
import {
  isStoreReviewStaleAfterAmendment,
  needsStoreIntentAction
} from '../../utils/workOrderStoreReview';
import { useToast } from '../../contexts/ToastContext';

export const canViewActionTakenForm = (userRole) =>
  [
    ROLES.MRO_ENGINEER,
    ROLES.MRO_MANAGER,
    ROLES.SUPER_ADMIN,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.CA_MANAGER,
    ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.MRO_QM_MANAGER,
    ROLES.SPECTATOR
  ].includes(userRole);

const getStatusColor = (status) => {
  const colors = {
    'SCHEDULED': 'warning',
    'HOLD': 'default',
    'IN_PROGRESS': 'info',
    'PARTS_PROCURED': 'info',
    'COMPLETED': 'success',
    'CANCELLED': 'error'
  };
  return colors[status] || 'default';
};

export const getNextAction = (workOrder, userRole) => {
  const wo = workOrder;
  const toDateKey = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  };
  const storeDateGateOpen = Boolean(
    wo.storeRejectedAt &&
    wo.storeAvailabilityDate &&
    !isStoreReviewStaleAfterAmendment(wo)
  );
  const scheduledDateKey = toDateKey(wo.scheduledDate);
  const storeAvailabilityDateKey = toDateKey(wo.storeAvailabilityDate);
  const planningDateAdjustedAfterStore = Boolean(
    storeDateGateOpen &&
    scheduledDateKey &&
    storeAvailabilityDateKey &&
    scheduledDateKey > storeAvailabilityDateKey
  );
  
  // Step 1: CAMO creates WO - always done if WO exists
  if (!wo.camoQmAcknowledged && wo.camoQmRejectedAt) {
    return {
      action: 'Work Order Revision Required',
      by: 'CAMO',
      description: `CAMO_QM rejected this work order. Please revise and resubmit. Remarks: ${wo.camoQmRejectionRemarks || 'N/A'}`,
      step: 1,
      canPerform: [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
    };
  }

  // Step 2: CAMO_QM Acknowledgment (only when not awaiting CAMO rework after rejection)
  if (!wo.camoQmAcknowledged && !wo.camoQmRejectedAt) {
    return {
      action: 'CAMO_QM Acknowledgment Required',
      by: 'CAMO_QM_MANAGER',
      description: 'CAMO_QM must acknowledge work order creation before Planning can act',
      step: 2,
      canPerform: [ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
    };
  }
  
  // Step 3: Planning Approval
  if (!wo.planningApproved) {
    if (wo.planningRejectedAt) {
      return {
        action: 'Planning Rework Required',
        by: 'CAMO_QM / CAMO',
        description: `Planning rejected and moved workflow to previous step. Remarks: ${wo.planningRejectionRemarks || 'N/A'}`,
        step: 2,
        canPerform: [ROLES.CAMO_QM_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
      };
    }
    return {
      action: 'Planning Approval Required',
      by: 'PLANNING_MANAGER',
      description: 'Planning must review and approve work order, set scheduled date, and indicate Store/MRO actions needed',
      step: 3,
      canPerform: userRole === ROLES.PLANNING_MANAGER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
    };
  }
  
  // Step 4: Store Accept/Reject (if needed)
  if (wo.needsStoreAction && needsStoreIntentAction(wo)) {
    return {
      action: 'Store Action Required',
      by: 'STORE_INSPECTOR',
      description: isStoreReviewStaleAfterAmendment(wo)
        ? 'Planning amended material requirements after Store action. Store must re-review the updated PO intent'
        : 'Store must accept or set unavailable-parts availability date',
      step: 4,
      canPerform: userRole === ROLES.STORE_INSPECTOR || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
    };
  }
  if (wo.needsStoreAction && wo.storeRejectedAt && !isStoreReviewStaleAfterAmendment(wo)) {
    if (!planningDateAdjustedAfterStore) {
      return {
        action: 'Planning Reschedule Required',
        by: 'PLANNING_MANAGER',
        description: `Store set parts availability date ${wo.storeAvailabilityDate ? dayjs(wo.storeAvailabilityDate).format('YYYY-MM-DD') : ''}. Planning must set a later maintenance date before MRO can proceed.`,
        step: 4.5,
        canPerform: userRole === ROLES.PLANNING_MANAGER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
      };
    }
    return {
      action: 'MRO Acceptance Required',
      by: 'MRO_MANAGER',
      description: 'Planning rescheduled after Store availability date. Workflow can proceed to MRO.',
      step: 6,
      canPerform: userRole === ROLES.MRO_MANAGER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
    };
  }
  
  // Step 5: Parts Procurement (if needed) - handled separately
  // Step 5.5: External Agency documents + completion (alternative / parallel to MRO execution)
  if (wo.needsExternalAgencyAction && !wo.externalAgencyCompleted) {
    const storeBlocking = wo.needsStoreAction && needsStoreIntentAction(wo);
    const mroBlocking = wo.needsMROAction && !wo.mroAccepted && !wo.mroRejectedAt;
    if (!storeBlocking && !mroBlocking) {
      return {
        action: 'External Agency Documents Required',
        by: 'PLANNING / CAMO',
        description: `Upload CRS, CAME form, and other documents from ${wo.externalAgencyName || 'external agency'}, then submit to CAMO QM completion`,
        step: 5.5,
        canPerform: [
          ROLES.PLANNING_MANAGER,
          ROLES.CA_MANAGER,
          ROLES.CA_TECHNICAL_OFFICER,
          ROLES.CAMO_QM_MANAGER,
          ROLES.CAMO_QM_OFFICER,
          ROLES.SUPER_ADMIN,
          ROLES.ACCOUNTABLE_MANAGER
        ].includes(userRole)
      };
    }
  }

  // Step 6: MRO Accept/Reject (if needed)
  if (wo.needsMROAction && !wo.mroAccepted && !wo.mroRejectedAt) {
    return {
      action: 'MRO Acceptance Required',
      by: 'MRO_MANAGER',
      description: 'MRO must accept or reject work order',
      step: 6,
      canPerform: userRole === ROLES.MRO_MANAGER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
    };
  }
  if (wo.needsMROAction && wo.mroRejectedAt) {
    return {
      action: 'Planning Rework Required',
      by: 'PLANNING_MANAGER',
      description: `MRO rejected and sent workflow back to Planning. Remarks: ${wo.mroRejectionNotes || 'N/A'}`,
      step: 3,
      canPerform: userRole === ROLES.PLANNING_MANAGER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
    };
  }
  
  // Step 7: MRO_ENGINEER Completion
  if (wo.mroAccepted && !wo.mroEngineerCompleted && wo.status === 'IN_PROGRESS') {
    if (wo.mroEngineerRejectedAt) {
      return {
        action: 'MRO Acceptance Required',
        by: 'MRO_MANAGER',
        description: `MRO_ENGINEER rejected and moved workflow back to MRO acceptance. Remarks: ${wo.mroEngineerRejectionRemarks || 'N/A'}`,
        step: 6,
        canPerform: userRole === ROLES.MRO_MANAGER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
      };
    }
    return {
      action: 'MRO_ENGINEER Completion Required',
      by: 'MRO_ENGINEER',
      description: 'MRO_ENGINEER must complete maintenance work',
      step: 7,
      canPerform: userRole === ROLES.MRO_ENGINEER || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ACCOUNTABLE_MANAGER
    };
  }
  
  // Step 8: MRO QM Completion Acknowledgment
  if (wo.mroEngineerCompleted && !wo.mroQmCompletionAcknowledged && wo.status === 'IN_PROGRESS') {
    if (wo.mroQmCompletionRejectedAt) {
      return {
        action: 'MRO QM Completion Acknowledgment Required',
        by: 'MRO_QM_MANAGER',
        description: `MRO QM rejected completion acknowledgment. Remarks: ${wo.mroQmCompletionRejectionRemarks || 'N/A'}`,
        step: 8,
        canPerform: [ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
      };
    }
    return {
      action: 'MRO QM Completion Acknowledgment Required',
      by: 'MRO_QM_MANAGER',
      description: 'MRO QM must acknowledge completion of maintenance work',
      step: 8,
      canPerform: [ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
    };
  }

  // Step 9: CAMO_QM Completion Acknowledgment
  if (wo.mroEngineerCompleted && wo.mroQmCompletionAcknowledged && !wo.camoQmCompletionAcknowledged && wo.status === 'IN_PROGRESS') {
    if (wo.camoQmCompletionRejectedAt) {
      return {
        action: 'MRO QM Completion Acknowledgment Required',
        by: 'MRO_QM_MANAGER',
        description: `CAMO_QM rejected completion and moved workflow back to previous step. Remarks: ${wo.camoQmCompletionRejectionRemarks || 'N/A'}`,
        step: 8,
        canPerform: [ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
      };
    }
    return {
      action: 'CAMO_QM Completion Acknowledgment Required',
      by: 'CAMO_QM_MANAGER',
      description: 'CAMO_QM must acknowledge completion of maintenance work',
      step: 9,
      canPerform: [ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
    };
  }
  
  // Step 10: Final Completion
  if (wo.mroEngineerCompleted && wo.mroQmCompletionAcknowledged && wo.camoQmCompletionAcknowledged && wo.status === 'IN_PROGRESS') {
    return {
      action: 'Mark Work Order as Completed',
      by: 'CAMO / MRO',
      description: 'Work order can be marked as COMPLETED after MRO_ENGINEER, MRO QM, and CAMO_QM approvals',
      step: 10,
      canPerform: [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(userRole)
    };
  }
  
  // If completed
  if (wo.status === 'COMPLETED') {
    return {
      action: 'Work Order Completed',
      by: 'N/A',
      description: 'Work order is completed. Maintenance release certificate can be issued.',
      step: 10,
      canPerform: false
    };
  }
  
  // If on hold (parts procurement)
  if (wo.status === 'HOLD') {
    return {
      action: 'Awaiting Parts Procurement',
      by: 'STORE_INSPECTOR',
      description: 'Work order is on hold. Waiting for parts procurement work order to complete.',
      step: 5,
      canPerform: false
    };
  }
  
  // Default
  return {
    action: 'Review Work Order Status',
    by: 'CAMO',
    description: 'Review current status and determine next steps',
    step: 0,
    canPerform: false
  };
};

const formatActorName = (actor) => {
  if (!actor) return 'System';
  if (typeof actor === 'string') return 'Unknown User';
  const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
  return name || actor.email || 'Unknown User';
};

const formatDateTime = (date, displayTimezone) => {
  if (!date) return 'N/A';
  return displayTimezone ? formatDateTimeWithTimezone(date, null, displayTimezone) : dayjs(date).format('DD MMM YYYY, HH:mm');
};

const formatSimpleName = (person) => {
  if (!person) return 'N/A';
  if (typeof person === 'string') return person;
  return `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email || 'N/A';
};

const MaintenanceWorkflowStatusCard = ({ workOrder, user, displayTimezone, onWorkOrderRefresh }) => {
  const { showToast } = useToast();
  const [actionTakenFormOpen, setActionTakenFormOpen] = useState(false);
  const [workOrderForForm, setWorkOrderForForm] = useState(workOrder);
  const [viewingActionTakenPdf, setViewingActionTakenPdf] = useState(false);
  const [downloadingActionTakenPdf, setDownloadingActionTakenPdf] = useState(false);
  const [downloadingCameFormPdf, setDownloadingCameFormPdf] = useState(false);

  useEffect(() => {
    setWorkOrderForForm(workOrder);
  }, [workOrder]);

  if (!workOrder) return null;

  const handleAuthenticatedDownload = async (url, filenameFallback) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filenameFallback);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const actionTakenForm = workOrder?.workOrderSecondPage;
  const hasActionTakenForm = Boolean(actionTakenForm?.savedAt && actionTakenForm?.form);
  const actionTakenSavedBy =
    actionTakenForm?.savedBy && typeof actionTakenForm.savedBy === 'object'
      ? `${actionTakenForm.savedBy.firstName || ''} ${actionTakenForm.savedBy.lastName || ''}`.trim()
      : '';
  const actionTakenPdfFilename = `Action-Taken-Form_${(workOrder.workOrderNumber || workOrder._id || 'work-order')
    .toString()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`;
  const showActionTakenSection =
    canViewActionTakenForm(user?.role) && workOrder.status !== 'CANCELLED';

  const actionTakenPdfUrl = workOrder?._id
    ? `/api/maintenance/${workOrder._id}/action-taken-form.pdf`
    : null;

  const handleViewActionTakenPdf = async () => {
    if (!actionTakenPdfUrl || !hasActionTakenForm) return;
    setViewingActionTakenPdf(true);
    try {
      await viewAuthenticatedPdf(actionTakenPdfUrl);
    } catch (err) {
      try {
        openAuthenticatedPdfViaToken(actionTakenPdfUrl);
      } catch (fallbackErr) {
        showToast(err.message || fallbackErr.message || 'Failed to open Action Taken Form PDF', 'error');
      }
    } finally {
      setViewingActionTakenPdf(false);
    }
  };

  const handleDownloadActionTakenPdf = async () => {
    if (!actionTakenPdfUrl || !hasActionTakenForm) return;
    setDownloadingActionTakenPdf(true);
    try {
      await downloadAuthenticatedPdf(`${actionTakenPdfUrl}?download=1`, actionTakenPdfFilename);
    } catch (err) {
      showToast(err.message || 'Failed to download Action Taken Form PDF', 'error');
    } finally {
      setDownloadingActionTakenPdf(false);
    }
  };

  const cameFormPdfUrl = workOrder?._id
    ? `/api/maintenance/${workOrder._id}/came-work-order-form.pdf`
    : null;
  const cameFormPdfFilename = `CAME-FORM-04_${(workOrder.workOrderNumber || workOrder._id || 'work-order')
    .toString()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`;

  const handleDownloadCameFormPdf = async () => {
    if (!cameFormPdfUrl) return;
    setDownloadingCameFormPdf(true);
    try {
      await downloadAuthenticatedPdf(`${cameFormPdfUrl}?download=1`, cameFormPdfFilename);
    } catch (err) {
      showToast(err.message || 'Failed to download CAME/FORM/04 PDF', 'error');
    } finally {
      setDownloadingCameFormPdf(false);
    }
  };

  const refreshWorkOrderAfterFormSave = async () => {
    if (!workOrder?._id) return;
    try {
      const res = await axios.get(`/api/maintenance/${workOrder._id}`);
      setWorkOrderForForm(res.data);
      if (typeof onWorkOrderRefresh === 'function') {
        await onWorkOrderRefresh(res.data);
      }
    } catch (err) {
      console.error('Failed to refresh work order after Action Taken Form save', err);
    }
  };

  const currentStatus = workOrder.status || 'UNKNOWN';
  const nextAction = getNextAction(workOrder, user?.role);
  
  // Get workflow steps with completion status
  const getWorkflowSteps = () => {
    const steps = [];
    
    // Step 1: Work Order Creation (always done)
    steps.push({
      step: 1,
      label: 'Work Order Created',
      description: 'CAMO created work order',
      completed: true,
      completedBy: workOrder.createdBy,
      completedAt: workOrder.createdAt,
      actor: 'CAMO'
    });
    
    // Linked intimations (from standalone Intimation flow)
    if (workOrder.linkedIntimationIds && workOrder.linkedIntimationIds.length > 0) {
      const linkedLabels = workOrder.linkedIntimationIds.map((int) => int.title || int._id?.toString().slice(-6) || 'Intimation').join(', ');
      steps.push({
        step: 1.5,
        label: 'Linked intimations',
        description: `This work order fulfils ${workOrder.linkedIntimationIds.length} intimation(s): ${linkedLabels}`,
        completed: true,
        completedBy: null,
        completedAt: null,
        actor: null
      });
    }

    // Step 2: CAMO_QM Acknowledgment
    steps.push({
      step: 2,
      label: 'CAMO_QM Acknowledgment',
      description: 'CAMO_QM acknowledges work order creation',
      completed: Boolean(workOrder.camoQmAcknowledged),
      rejected: !!workOrder.camoQmRejectedAt && !workOrder.camoQmAcknowledged,
      completedBy: workOrder.camoQmAcknowledged ? workOrder.camoQmAcknowledgedBy : workOrder.camoQmRejectedBy,
      completedAt: workOrder.camoQmAcknowledged ? workOrder.camoQmAcknowledgedAt : workOrder.camoQmRejectedAt,
      actor: 'CAMO_QM_MANAGER',
      notes: workOrder.camoQmAcknowledged ? workOrder.camoQmNotes : workOrder.camoQmRejectionRemarks
    });
    
    // Step 3: Planning Approval
    steps.push({
      step: 3,
      label: 'Planning Approval',
      description: 'Planning reviews and approves work order',
      completed: workOrder.planningApproved || !!workOrder.planningRejectedAt,
      rejected: !!workOrder.planningRejectedAt,
      completedBy: workOrder.planningApproved ? workOrder.planningApprovedBy : workOrder.planningRejectedBy,
      completedAt: workOrder.planningApproved ? workOrder.planningApprovedAt : workOrder.planningRejectedAt,
      actor: 'PLANNING_MANAGER',
      notes: workOrder.planningApproved ? workOrder.planningNotes : workOrder.planningRejectionRemarks,
      scheduledDate: workOrder.scheduledDate
    });
    
    // Step 4: Store Action (if needed)
    if (workOrder.needsStoreAction) {
      const storeDateGateOpen = Boolean(
        workOrder.storeRejectedAt &&
        workOrder.storeAvailabilityDate &&
        !isStoreReviewStaleAfterAmendment(workOrder)
      );
      const plannedDateKey = workOrder.scheduledDate ? new Date(workOrder.scheduledDate).toISOString().slice(0, 10) : null;
      const availableFromKey = workOrder.storeAvailabilityDate ? new Date(workOrder.storeAvailabilityDate).toISOString().slice(0, 10) : null;
      const planningDateAdjustedAfterStore = Boolean(
        storeDateGateOpen &&
        plannedDateKey &&
        availableFromKey &&
        plannedDateKey > availableFromKey
      );

      steps.push({
        step: 4,
        label: 'Store Action',
        description: 'Store accepts or sets unavailable-parts availability date',
        completed: needsStoreIntentAction(workOrder)
          ? false
          : Boolean(workOrder.storeAccepted || workOrder.storeRejectedAt),
        rejected: false,
        completedBy: workOrder.storeAccepted ? workOrder.storeAcceptedBy : workOrder.storeAcceptedBy,
        completedAt: workOrder.storeAcceptedAt || workOrder.storeRejectedAt,
        actor: 'STORE_INSPECTOR',
        notes: needsStoreIntentAction(workOrder) && isStoreReviewStaleAfterAmendment(workOrder)
          ? 'Planning amended requirements after prior Store action — re-review required'
          : workOrder.storeAccepted
          ? workOrder.storeNotes
          : [workOrder.storeRejectionNotes, workOrder.storeAvailabilityDate ? `Available from: ${formatDateTime(workOrder.storeAvailabilityDate, displayTimezone)}` : '']
              .filter(Boolean)
              .join(' | ')
      });

      if (storeDateGateOpen) {
        steps.push({
          step: 4.5,
          label: 'Planning Reschedule After Store Date',
          description: 'Planning must set scheduled date later than Store availability date before MRO',
          completed: planningDateAdjustedAfterStore,
          completedBy: planningDateAdjustedAfterStore ? (workOrder.scheduledDateSetBy || workOrder.planningApprovedBy) : null,
          completedAt: planningDateAdjustedAfterStore ? (workOrder.scheduledDateSetAt || workOrder.updatedAt) : null,
          actor: 'PLANNING_MANAGER',
          notes: `Store available from: ${formatDateTime(workOrder.storeAvailabilityDate, displayTimezone)}`
        });
      }
    }
    
    // Step 5: Parts Procurement (if exists)
    if (workOrder.linkedWorkOrderId || workOrder.status === 'HOLD') {
      steps.push({
        step: 5,
        label: 'Parts Procurement',
        description: 'Parts procurement work order (if parts not in inventory)',
        completed: workOrder.status !== 'HOLD',
        completedBy: null,
        completedAt: null,
        actor: 'STORE_INSPECTOR / PLANNING_MANAGER',
        isPartsProcurement: true
      });
    }
    
    // Step 6: MRO Acceptance (if needed)
    if (workOrder.needsMROAction) {
      const assignmentBits = [];
      const currentOwner = workOrder.mroCurrentEngineerId || workOrder.mroAssignedEngineerId;
      if (currentOwner) {
        assignmentBits.push(`Current Engineer: ${formatSimpleName(currentOwner)}`);
      }
      if (Array.isArray(workOrder.mroAssignedEngineerIds) && workOrder.mroAssignedEngineerIds.length > 1) {
        assignmentBits.push(`Team: ${workOrder.mroAssignedEngineerIds.map(formatSimpleName).filter(Boolean).join(', ')}`);
      } else if (workOrder.mroAssignedEngineerId && !workOrder.mroCurrentEngineerId) {
        assignmentBits.push(`Assigned Engineer: ${formatSimpleName(workOrder.mroAssignedEngineerId)}`);
      }
      if (workOrder.mroAssignedDate) {
        assignmentBits.push(`Assigned Date: ${formatDateTime(workOrder.mroAssignedDate, displayTimezone)}`);
      }
      const assignmentText = assignmentBits.join(' | ');
      steps.push({
        step: 6,
        label: 'MRO Acceptance',
        description: 'MRO accepts or rejects work order',
        completed: workOrder.mroAccepted || workOrder.mroRejectedAt ? true : false,
        rejected: !!workOrder.mroRejectedAt,
        completedBy: workOrder.mroAcceptedBy,
        completedAt: workOrder.mroAcceptedAt || workOrder.mroRejectedAt,
        actor: 'MRO_MANAGER',
        notes: [workOrder.mroAccepted ? workOrder.mroNotes : workOrder.mroRejectionNotes, assignmentText]
          .filter(Boolean)
          .join(' | ')
      });
    }
    
    // Step 7: MRO_ENGINEER Completion
    if (workOrder.mroAccepted || workOrder.mroEngineerRejectedAt) {
      steps.push({
        step: 7,
        label: 'MRO_ENGINEER Completion',
        description: 'MRO_ENGINEER completes maintenance work',
        completed: workOrder.mroEngineerCompleted || !!workOrder.mroEngineerRejectedAt,
        rejected: !!workOrder.mroEngineerRejectedAt,
        completedBy: workOrder.mroEngineerCompleted ? workOrder.mroEngineerCompletedBy : workOrder.mroEngineerRejectedBy,
        completedAt: workOrder.mroEngineerCompleted ? workOrder.mroEngineerCompletedAt : workOrder.mroEngineerRejectedAt,
        actor: 'MRO_ENGINEER',
        notes: workOrder.mroEngineerCompleted ? workOrder.mroEngineerNotes : workOrder.mroEngineerRejectionRemarks
      });
    }
    
    // Step 8: MRO QM Completion Acknowledgment
    if (workOrder.mroEngineerCompleted || workOrder.mroQmCompletionRejectedAt) {
      steps.push({
        step: 8,
        label: 'MRO QM Completion Acknowledgment',
        description: 'MRO QM acknowledges completion',
        completed: workOrder.mroQmCompletionAcknowledged || !!workOrder.mroQmCompletionRejectedAt,
        rejected: !!workOrder.mroQmCompletionRejectedAt,
        completedBy: workOrder.mroQmCompletionAcknowledged ? workOrder.mroQmCompletionAcknowledgedBy : workOrder.mroQmCompletionRejectedBy,
        completedAt: workOrder.mroQmCompletionAcknowledged ? workOrder.mroQmCompletionAcknowledgedAt : workOrder.mroQmCompletionRejectedAt,
        actor: 'MRO_QM_MANAGER',
        notes: workOrder.mroQmCompletionAcknowledged ? workOrder.mroQmCompletionNotes : workOrder.mroQmCompletionRejectionRemarks
      });
    }
    
    // Step 9: CAMO_QM Completion Acknowledgment
    if (workOrder.mroQmCompletionAcknowledged || workOrder.camoQmCompletionRejectedAt) {
      steps.push({
        step: 9,
        label: 'CAMO_QM Completion Acknowledgment',
        description: 'CAMO_QM acknowledges completion',
        completed: workOrder.camoQmCompletionAcknowledged || !!workOrder.camoQmCompletionRejectedAt,
        rejected: !!workOrder.camoQmCompletionRejectedAt,
        completedBy: workOrder.camoQmCompletionAcknowledged ? workOrder.camoQmCompletionAcknowledgedBy : workOrder.camoQmCompletionRejectedBy,
        completedAt: workOrder.camoQmCompletionAcknowledged ? workOrder.camoQmCompletionAcknowledgedAt : workOrder.camoQmCompletionRejectedAt,
        actor: 'CAMO_QM_MANAGER',
        notes: workOrder.camoQmCompletionAcknowledged ? workOrder.camoQmCompletionNotes : workOrder.camoQmCompletionRejectionRemarks
      });
    }
    
    // Step 10: Final Completion
    steps.push({
      step: 10,
      label: 'Work Order Completion',
      description: 'Work order marked as COMPLETED',
      completed: workOrder.status === 'COMPLETED',
      completedBy: null,
      completedAt: workOrder.completedDate,
      actor: 'CAMO / MRO'
    });
    
    return steps;
  };
  
  const workflowSteps = getWorkflowSteps();
  const lastCompletedStep = workflowSteps.filter(s => s.completed).length;
  const statusHistory = workOrder.statusHistory || [];
  const lastStatusEntry = statusHistory.length > 0 ? statusHistory[statusHistory.length - 1] : null;
  
  return (
    <Card sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Workflow Status</Typography>
        </Box>
        
        <Grid container spacing={2}>
          {/* Current Status */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Status
            </Typography>
            <Chip 
              label={currentStatus.replace(/_/g, ' ')} 
              color={getStatusColor(currentStatus)}
              size="small"
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              WO #{workOrder.workOrderNumber || workOrder.woNumber || (workOrder._id && workOrder._id.toString().slice(-6))}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              Due Date (CAMO): {formatDateTime(workOrder.dueDate || workOrder.scheduledDate, displayTimezone)}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              Scheduled:{' '}
              {workOrder.scheduleMode === 'HOURLY' && workOrder.scheduledEndDate
                ? (() => {
                    const start = new Date(workOrder.scheduledDate);
                    const end = new Date(workOrder.scheduledEndDate);
                    const sameDay = start.toDateString() === end.toDateString();
                    const range = `${formatDateTime(workOrder.scheduledDate, displayTimezone)} – ${formatDateTime(workOrder.scheduledEndDate, displayTimezone)}`;
                    return `${range} (${sameDay ? 'same-day window' : 'multi-day window'})`;
                  })()
                : `${formatDateTime(workOrder.scheduledDate || workOrder.dueDate, displayTimezone)} (full day)`}
            </Typography>
          </Grid>

          {/* Last Action */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Last Action
            </Typography>
            {lastStatusEntry ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {lastStatusEntry.status?.replace(/_/g, ' ') || 'Status Updated'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  <PersonIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  {formatActorName(lastStatusEntry.actorId)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDateTime(lastStatusEntry.changedAt, displayTimezone)}
                </Typography>
                {lastStatusEntry.remarks && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    "{lastStatusEntry.remarks}"
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No actions recorded yet
              </Typography>
            )}
          </Grid>

          {/* Next Action */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Next Action Required
            </Typography>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: nextAction.canPerform ? 'success.light' : 'info.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: nextAction.canPerform ? 'success.main' : 'info.main'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <ArrowForwardIcon sx={{ fontSize: 16, mr: 0.5, color: nextAction.canPerform ? 'success.main' : 'info.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {nextAction.action}
                </Typography>
              </Box>
              <Typography variant="caption" display="block" color="text.secondary">
                By: {nextAction.by}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {nextAction.description}
              </Typography>
              {nextAction.canPerform && (
                <Chip 
                  icon={<CheckCircleIcon />}
                  label="You can perform this action"
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
        {workOrder?.crsDocument?.originalName && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              CRS Document
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleAuthenticatedDownload(
                `/api/maintenance/${workOrder._id}/crs/download`,
                workOrder.crsDocument.originalName || `crs-${workOrder._id}`
              )}
            >
              Download CRS ({workOrder.crsDocument.originalName})
            </Button>
          </Box>
        )}
        {workOrder?.workOrderCsvDocument?.originalName && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Maintenance CSV
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleAuthenticatedDownload(
                `/api/maintenance/${workOrder._id}/work-order-csv/download`,
                workOrder.workOrderCsvDocument.originalName || `work-order-${workOrder._id}.csv`
              )}
            >
              Download CSV ({workOrder.workOrderCsvDocument.originalName})
            </Button>
          </Box>
        )}
        {showActionTakenSection && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              CAME/FORM/04 — MRO QM work package
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCameFormPdf}
                disabled={downloadingCameFormPdf}
              >
                {downloadingCameFormPdf ? 'Downloading…' : 'Download CAME/FORM/04'}
              </Button>
            </Stack>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Action Taken Form
            </Typography>
            {hasActionTakenForm ? (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                Saved{' '}
                {formatDateTimeWithTimezone(actionTakenForm.savedAt, displayTimezone)}
                {actionTakenSavedBy ? ` by ${actionTakenSavedBy}` : ''}
              </Typography>
            ) : (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                No saved form yet. Open to create or edit the Action Taken Form.
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => setActionTakenFormOpen(true)}
              >
                View Action Taken Form
              </Button>
              {hasActionTakenForm && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleViewActionTakenPdf}
                    disabled={viewingActionTakenPdf || downloadingActionTakenPdf}
                  >
                    {viewingActionTakenPdf ? 'Opening…' : 'View PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadActionTakenPdf}
                    disabled={viewingActionTakenPdf || downloadingActionTakenPdf}
                  >
                    {downloadingActionTakenPdf ? 'Downloading…' : 'Download PDF'}
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        )}
        
        {/* Workflow Steps List */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
            Workflow Steps
          </Typography>
          <List>
            {workflowSteps.map((step, index) => (
              <React.Fragment key={step.step}>
                <ListItem>
                  <ListItemIcon>
                    {step.completed ? (
                      step.rejected ? (
                        <CancelIcon color="error" />
                      ) : (
                        <CheckCircleOutlineIcon color="success" />
                      )
                    ) : (
                      <PendingIcon color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: step.completed ? 'medium' : 'normal' }}>
                          Step {step.step}: {step.label}
                        </Typography>
                        {step.completed && !step.rejected && (
                          <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                        {step.rejected && (
                          <Chip label="Rejected" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                        {!step.completed && index === lastCompletedStep && (
                          <Chip label="Pending" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {step.description}
                        </Typography>
                        {step.completed && step.completedBy && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                            By: {formatActorName(step.completedBy)} • {formatDateTime(step.completedAt, displayTimezone)}
                          </Typography>
                        )}
                        {step.rejected && step.completedBy && (
                          <Typography variant="caption" display="block" color="error.main" sx={{ mt: 0.5 }}>
                            <CancelIcon sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                            Rejected by: {formatActorName(step.completedBy)} • {formatDateTime(step.completedAt, displayTimezone)}
                          </Typography>
                        )}
                        {step.scheduledDate && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            Scheduled: {formatDateTime(step.scheduledDate, displayTimezone)}
                          </Typography>
                        )}
                        {step.notes && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            Notes: {step.notes}
                          </Typography>
                        )}
                        {step.includedItems && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>Included:</strong> {step.includedItems}
                          </Typography>
                        )}
                        {step.excludedItems && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>Excluded:</strong> {step.excludedItems}
                          </Typography>
                        )}
                        {step.workOrderNumber && (
                          <Typography variant="caption" display="block" color="success.main" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                            Work Order Number: {step.workOrderNumber}
                          </Typography>
                        )}
                        {step.isPartsProcurement && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {workOrder.status === 'HOLD' ? 'Parts procurement in progress...' : 'Parts procurement completed'}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < workflowSteps.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Box>
        
        {/* Workflow Progress Indicator */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">View Workflow Progress Timeline</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MaintenanceWorkflowProgressIndicator 
              currentStatus={currentStatus}
              workOrder={workOrder}
            />
          </AccordionDetails>
        </Accordion>
      </CardContent>
      {actionTakenFormOpen && workOrderForForm && (
        <WorkOrderSecondPageDialog
          open={actionTakenFormOpen}
          onClose={() => setActionTakenFormOpen(false)}
          workOrder={workOrderForForm}
          user={user}
          onSaved={refreshWorkOrderAfterFormSave}
        />
      )}
    </Card>
  );
};

export default MaintenanceWorkflowStatusCard;

